import { Link, useNavigate } from "react-router-dom";
import { useCallback, useRef, useState } from "react";
import TRAIcon from "../../../design/icons/TRAIcon";
import { TRAButton, TRARecentFormBars } from "../../../design";
import {
  TRAEyebrow,
  TRAKpiStrip,
  TRAKpiStripCell,
  TRARecentMatchRow,
} from "../../../design/visual";
import { recentFormLetters } from "../../../domain/profile/profileDashboardStats";
import { useAuth } from "../../../hooks/useAuth";
import { enterResumedRoomSession } from "../../../services/session/enterResumedRoomSession";
import homeStyles from "../../match/lobby/styles/homeVisual.module.css";
import ArenaCreateJoinPanel from "../../lobby/components/ArenaCreateJoinPanel";
import lobbyStyles from "../../lobby/styles/lobby.module.css";
import HomeStartQuickMatchCourt from "./HomeStartQuickMatchCourt";
import styles from "../styles/playerExperience.module.css";

function LinkButton({ to, variant = "primary", children }) {
  const navigate = useNavigate();
  return (
    <TRAButton type="button" variant={variant} onClick={() => navigate(to)}>
      {children}
    </TRAButton>
  );
}

function truncateLabel(text, max = 26) {
  const t = (text || "").trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/**
 * Home top after greeting — Start Quick Match court / Host Tournament / Join / Resume.
 * Host Tournament only for org owners/admins (`canManageOrganization`).
 * Create / Join use the same inline slide panel as the Arena hub (no navigation).
 */
export function HomeTodayTopSections({
  resumeRoom,
  stats,
  recentMatches = [],
  canHostTournament = false,
}) {
  const navigate = useNavigate();
  const { uid } = useAuth();
  const [busy, setBusy] = useState(false);
  const [hubPanel, setHubPanel] = useState(null); // null | "create" | "join"
  const quickMatchRef = useRef(null);

  const season = stats?.season;
  const showSeason =
    season &&
    (Number(season.matches) > 0 ||
      Number(season.wins) > 0 ||
      Number(season.losses) > 0 ||
      season.winRate != null);
  const recentCard = recentMatches[0] || null;
  const showResume = Boolean(resumeRoom?.room);

  const toggleHubPanel = useCallback((mode) => {
    setHubPanel((prev) => (prev === mode ? null : mode));
  }, []);

  const onResume = async () => {
    if (!resumeRoom?.room || busy) return;
    setBusy(true);
    try {
      await enterResumedRoomSession({
        roomCode: resumeRoom.room,
        uid,
        navigate,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.homeTodayBlock} data-testid="home-today-top">
      <div className={styles.homeTodayLead}>
        <div className={styles.homeTodayActions} role="group" aria-label="Arena actions">
          <HomeStartQuickMatchCourt
            ref={quickMatchRef}
            expanded={hubPanel === "create"}
            onClick={() => toggleHubPanel("create")}
          />
          {canHostTournament ? (
            <button
              type="button"
              className={`${homeStyles.homeSecondaryBtn} ${styles.homeTodaySecondary} ${lobbyStyles.roomHubSecondaryLink}`}
              data-testid="home-host-tournament"
              onClick={() => navigate("/tournaments/new")}
            >
              <TRAIcon name="trophy" size="sm" />
              Host Tournament
            </button>
          ) : null}
          <div className={styles.homeTodaySecondaryRow}>
            <button
              type="button"
              className={`${homeStyles.homeSecondaryBtn} ${styles.homeTodaySecondary} ${lobbyStyles.roomHubSecondaryLink}${
                hubPanel === "join" ? ` ${lobbyStyles.roomHubCtaOn}` : ""
              }`}
              data-testid="home-join-arena"
              aria-expanded={hubPanel === "join"}
              aria-controls="home-create-join"
              onClick={() => toggleHubPanel("join")}
            >
              Join Arena
            </button>
            {showResume ? (
              <button
                type="button"
                className={`${homeStyles.homeResumeBtn} ${styles.homeTodaySecondary}`}
                disabled={busy}
                onClick={() => void onResume()}
                data-testid="home-resume-btn"
              >
                <span className={styles.homeTodayResumeLabel}>
                  <span className={homeStyles.homeResumeTitle}>
                    {busy ? "Opening…" : "Continue Playing"}
                  </span>
                  <span className={homeStyles.homeResumeSub}>
                    {truncateLabel(resumeRoom.roomDisplayName || resumeRoom.room)}
                  </span>
                </span>
              </button>
            ) : null}
          </div>
          <div id="home-create-join">
            <ArenaCreateJoinPanel
              open={hubPanel != null}
              mode={hubPanel === "join" ? "join" : "create"}
              onModeChange={(next) => setHubPanel(next)}
              focusFromRef={hubPanel === "create" ? quickMatchRef : null}
            />
          </div>
        </div>

        {showSeason ? (
          <div className={styles.homeTodayKpi} data-testid="home-your-season">
            <TRAEyebrow as="p">Your season</TRAEyebrow>
            <TRAKpiStrip columns={4}>
              <TRAKpiStripCell label="Matches" value={String(season.matches ?? 0)} divider={false} />
              <TRAKpiStripCell label="Wins" value={String(season.wins ?? 0)} divider />
              <TRAKpiStripCell label="Losses" value={String(season.losses ?? 0)} divider />
              <TRAKpiStripCell
                label="Win rate"
                value={season.winRate == null ? "—" : `${Math.round(season.winRate)}%`}
                accent={season.winRate != null}
                divider
              />
            </TRAKpiStrip>
          </div>
        ) : null}
      </div>

      <SeasonProgressCard stats={stats} />

      {recentCard?.matchId ? (
        <section className={styles.homeTodaySection} aria-labelledby="home-recent-heading">
          <TRAEyebrow as="h2" id="home-recent-heading">
            Recent activity
          </TRAEyebrow>
          <TRARecentMatchRow
            card={recentCard}
            to={`/history/match/${encodeURIComponent(recentCard.matchId)}`}
          />
        </section>
      ) : null}
    </div>
  );
}

/** @deprecated Prefer HomeTodayTopSections — kept for callers that still import pills. */
export function QuickActionGrid() {
  return (
    <div className={styles.pillRow} role="group" aria-label="Quick actions">
      <Link className={`${styles.pill} ${styles.pillPrimary}`} to="/arena?create=1">
        <TRAIcon name="plus" size="sm" />
        Create Arena
      </Link>
      <Link className={`${styles.pill} ${styles.pillMuted}`} to="/arena?join=1">
        <TRAIcon name="users" size="sm" />
        Join Arena
      </Link>
    </div>
  );
}

/** Continue Playing — one-click resume into Room Session (no password when granted). */
export function ContinuePlayingCard({ resumeRoom, card }) {
  const navigate = useNavigate();
  const { uid } = useAuth();
  const [busy, setBusy] = useState(false);

  if (!resumeRoom?.room && card?.kind !== "resume") return null;

  const roomCode = resumeRoom?.room || (card?.href?.match(/\/room\/([^/?#]+)/)?.[1] ?? null);
  const title =
    card?.title || resumeRoom?.roomDisplayName || resumeRoom?.room || "Match arena";
  const subtitle = card?.subtitle || "Continue scoring in Arena Experience";

  const onResume = async () => {
    if (!roomCode || busy) return;
    setBusy(true);
    try {
      await enterResumedRoomSession({ roomCode, uid, navigate });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="continue-playing-heading">
      <div className={styles.sectionHead}>
        <h2 id="continue-playing-heading" className={styles.sectionTitle}>
          Continue playing
        </h2>
      </div>
      <article className={styles.card} data-testid="continue-playing-card">
        <span className={styles.badge}>Active arena</span>
        <h3 className={styles.nextTitle}>{title}</h3>
        <p className={styles.nextMeta}>{subtitle}</p>
        <div className={styles.nextCta}>
          <TRAButton type="button" variant="primary" disabled={busy} onClick={() => void onResume()}>
            {busy ? "Opening…" : card?.ctaLabel || "Resume"}
          </TRAButton>
        </div>
      </article>
    </section>
  );
}

/** Today&apos;s upcoming room / match from registrations or next-action card. */
export function TodaysUpcomingSection({ registrations, nextCard }) {
  const upcoming = registrations?.upcoming || [];
  const current = registrations?.current || [];
  const first = upcoming[0] || current[0] || null;

  if (!first && !nextCard) return null;

  if (nextCard && nextCard.kind !== "resume") {
    return (
      <section aria-labelledby="todays-upcoming-heading">
        <div className={styles.sectionHead}>
          <h2 id="todays-upcoming-heading" className={styles.sectionTitle}>
            Today&apos;s upcoming
          </h2>
        </div>
        <NextMatchCard card={nextCard} />
      </section>
    );
  }

  if (!first) return null;

  const id = first.competitionId || first.id;
  return (
    <section aria-labelledby="todays-upcoming-heading">
      <div className={styles.sectionHead}>
        <h2 id="todays-upcoming-heading" className={styles.sectionTitle}>
          Today&apos;s upcoming
        </h2>
      </div>
      <article className={styles.card} data-testid="todays-upcoming-card">
        <span className={styles.badge}>Upcoming</span>
        <h3 className={styles.nextTitle}>{first.name || "Tournament"}</h3>
        <p className={styles.nextMeta}>Registered event</p>
        <div className={styles.nextCta}>
          <LinkButton to={id ? `/browse/${encodeURIComponent(id)}` : "/discover"} variant="primary">
            View
          </LinkButton>
        </div>
      </article>
    </section>
  );
}

export function TodaysJourney({ items = [] }) {
  if (!items.length) return null;
  return (
    <section aria-labelledby="todays-journey-heading">
      <div className={styles.sectionHead}>
        <h2 id="todays-journey-heading" className={styles.sectionTitle}>
          Today&apos;s journey
        </h2>
      </div>
      <ul className={styles.journeyList}>
        {items.map((item) => {
          const body = (
            <>
              <div>
                <p className={styles.journeyTitle}>{item.title}</p>
                {item.detail ? <p className={styles.journeyDetail}>{item.detail}</p> : null}
              </div>
              {item.ctaLabel ? <span className={styles.linkAction}>{item.ctaLabel}</span> : null}
            </>
          );
          return (
            <li key={item.id}>
              {item.href ? (
                <Link className={styles.journeyItem} to={item.href}>
                  {body}
                </Link>
              ) : (
                <div className={styles.journeyItem}>{body}</div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function NextMatchCard({ card }) {
  if (!card) {
    return (
      <article className={styles.card} data-testid="next-match-card-empty">
        <span className={styles.badge}>Next up</span>
        <h3 className={styles.nextTitle}>No upcoming match yet</h3>
        <p className={styles.nextMeta}>
          Court call and fixture countdowns appear here when available. Create an arena or register for a
          tournament to get started.
        </p>
        <div className={styles.nextCta}>
          <LinkButton to="/browse" variant="primary">
            Browse tournaments
          </LinkButton>
        </div>
      </article>
    );
  }

  return (
    <article className={styles.card} data-testid="next-match-card">
      <div className={styles.nextHead}>
        <div>
          <span className={styles.badge}>{card.badge}</span>
          <h3 className={styles.nextTitle}>{card.title}</h3>
        </div>
        {card.countdownValue ? (
          <div className={styles.countdown}>
            <div className={styles.countdownValue}>{card.countdownValue}</div>
            {card.countdownLabel ? (
              <div className={styles.countdownLabel}>{card.countdownLabel}</div>
            ) : null}
          </div>
        ) : null}
      </div>
      <p className={styles.nextMeta}>
        {card.subtitle}
        {card.meta ? ` · ${card.meta}` : ""}
      </p>
      <div className={styles.nextCta}>
        <LinkButton to={card.href} variant="primary">
          {card.ctaLabel || "Open"}
        </LinkButton>
      </div>
    </article>
  );
}

export function SeasonProgressCard({ stats }) {
  const season = stats?.season;
  const hasSeason =
    season &&
    (Number(season.matches) > 0 ||
      Number(season.wins) > 0 ||
      Number(season.losses) > 0 ||
      season.winRate != null);
  if (!hasSeason) return null;
  const pct =
    season?.winRate != null
      ? Math.round(season.winRate)
      : season?.matches
        ? Math.round(((season.wins || 0) / Math.max(1, (season.wins || 0) + (season.losses || 0))) * 100)
        : null;
  const r = 40;
  const c = 2 * Math.PI * r;
  const offset = pct == null ? c : c * (1 - pct / 100);

  return (
    <article className={`${styles.card} ${styles.cardDark}`} data-testid="season-progress">
      <div className={styles.seasonInner}>
        <div className={styles.ringWrap} aria-hidden={pct == null}>
          <svg viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={r} fill="transparent" stroke="rgba(255,255,255,0.12)" strokeWidth="8" />
            <circle
              cx="50"
              cy="50"
              r={r}
              fill="transparent"
              stroke="var(--tra-color-primary)"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={c}
              strokeDashoffset={offset}
            />
          </svg>
          <div className={styles.ringCenter}>{pct != null ? `${pct}%` : "—"}</div>
        </div>
        <h3 className={styles.seasonTitle}>Season progress</h3>
        <p className={styles.seasonSub}>
          {season?.matches
            ? `${season.wins || 0} wins · ${season.losses || 0} losses`
            : "Play matches to track your season"}
        </p>
      </div>
    </article>
  );
}

export function RecentFormSection({ matches = [] }) {
  const letters = recentFormLetters(matches, 5);
  if (!letters.length) return null;
  return (
    <section aria-labelledby="how-playing-heading" data-testid="home-recent-form">
      <div className={styles.sectionHead}>
        <h2 id="how-playing-heading" className={styles.sectionTitle}>
          How you&apos;re playing
        </h2>
        <Link className={styles.linkAction} to="/my-player">
          Sports dashboard <TRAIcon name="chevronRight" size="sm" />
        </Link>
      </div>
      <TRARecentFormBars letters={letters} limit={5} padEmpty={false} />
    </section>
  );
}

export function RecentResultsCarousel({ matches = [], onViewAll }) {
  return (
    <section aria-labelledby="recent-results-heading">
      <div className={styles.sectionHead}>
        <h2 id="recent-results-heading" className={styles.sectionTitle}>
          Recent results
        </h2>
        {onViewAll ? (
          <button type="button" className={styles.linkAction} onClick={onViewAll}>
            View all <TRAIcon name="chevronRight" size="sm" />
          </button>
        ) : (
          <Link className={styles.linkAction} to="/profile/matches">
            View all <TRAIcon name="chevronRight" size="sm" />
          </Link>
        )}
      </div>
      {!matches.length ? (
        <p className={styles.muted}>No results yet — your wins and losses will show up here.</p>
      ) : (
        <div className={styles.hScroll}>
          {matches.map((m) => (
            <Link key={m.matchId} className={styles.resultCard} to={`/history/match/${m.matchId}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span className={`${styles.wlCircle} ${m.isWin ? styles.wlWin : styles.wlLoss}`}>
                  {m.isWin ? "W" : "L"}
                </span>
                <div>
                  <p className={styles.resultLabel}>{m.opponentsLabel || m.resultHeadline || "Match"}</p>
                  <p className={styles.resultMeta}>
                    {[m.completedAt ? new Date(m.completedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null, m.scoreSummary]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

export function CompetitionSpotlightCard({ competition }) {
  if (!competition) return null;

  const venue = [competition.venue?.city, competition.venue?.state, competition.venue?.name]
    .filter(Boolean)
    .join(" · ");

  return (
    <section aria-labelledby="featured-heading">
      <div className={styles.sectionHead}>
        <h2 id="featured-heading" className={styles.sectionTitle}>
          Suggested for you
        </h2>
        <Link className={styles.linkAction} to="/browse">
          See all
        </Link>
      </div>
      <article className={styles.spotlight} data-testid="competition-spotlight">
        <span className={`${styles.badge} ${styles.spotlightBadge}`}>
          {competition.registrationOpen ? "Open registration" : "Tournament"}
        </span>
        <h3 className={styles.spotlightTitle}>{competition.name}</h3>
        <p className={styles.spotlightMeta}>{venue || "Venue TBA"}</p>
        <div className={styles.spotlightActions}>
          {competition.registrationOpen ? (
            <LinkButton to={`/browse/${competition.competitionId}`} variant="primary">
              Register
            </LinkButton>
          ) : null}
          <LinkButton to={`/browse/${competition.competitionId}`} variant="secondary">
            Learn more
          </LinkButton>
        </div>
        <p className={styles.futureHint} style={{ color: "rgba(255,255,255,0.7)" }}>
          Open Challenges — reserved for a future wave.
        </p>
      </article>
    </section>
  );
}

export function RankingSummaryCard({ ranking }) {
  const s = ranking?.profileStats || {};
  const tiles = [
    { label: "Rank", value: s.currentRanking != null ? `#${s.currentRanking}` : "—" },
    { label: "Season pts", value: s.seasonPoints ?? ranking?.overall?.points ?? "—" },
    { label: "Titles", value: s.careerTitles ?? 0 },
    { label: "Win %", value: s.winPct != null ? `${s.winPct}%` : "—" },
  ];
  return (
    <section aria-labelledby="ranking-summary-heading">
      <div className={styles.sectionHead}>
        <h2 id="ranking-summary-heading" className={styles.sectionTitle}>
          Ranking
        </h2>
        <Link className={styles.linkAction} to="/rankings">
          Leaderboard
        </Link>
      </div>
      <div className={styles.rankRow}>
        {tiles.map((t) => (
          <div key={t.label} className={styles.rankTile}>
            <p className={styles.rankValue}>{t.value}</p>
            <p className={styles.rankLabel}>{t.label}</p>
          </div>
        ))}
      </div>
      {s.recentForm?.results?.length ? (
        <TRARecentFormBars form={s.recentForm} limit={5} caption="Last 5" padEmpty={false} />
      ) : null}
    </section>
  );
}

export function AchievementCarousel({ achievements = [] }) {
  if (!achievements.length) return null;
  return (
    <section aria-labelledby="achievements-heading">
      <div className={styles.sectionHead}>
        <h2 id="achievements-heading" className={styles.sectionTitle}>
          Achievements
        </h2>
        <Link className={styles.linkAction} to="/profile">
          Career
        </Link>
      </div>
      <div className={styles.achTrack}>
        {achievements.map((a) => (
          <article key={a.id} className={styles.achCard}>
            <h3>{a.title}</h3>
            <p>{a.detail}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

export function OrganizationSummary({ badges = [] }) {
  if (!badges.length) return null;
  return (
    <section aria-labelledby="org-summary-heading">
      <div className={styles.sectionHead}>
        <h2 id="org-summary-heading" className={styles.sectionTitle}>
          My organization
        </h2>
        <Link className={styles.linkAction} to="/organizations">
          Explore
        </Link>
      </div>
      <div className={styles.orgRow}>
        {badges.map((b) => (
          <Link
            key={b.organizationId}
            className={styles.orgChip}
            to={`/organization/${b.organizationId}`}
          >
            {b.name}
          </Link>
        ))}
      </div>
    </section>
  );
}

export function NotificationPreview({ items = [] }) {
  const preview = items.slice(0, 3);
  return (
    <section aria-labelledby="notify-preview-heading">
      <div className={styles.sectionHead}>
        <h2 id="notify-preview-heading" className={styles.sectionTitle}>
          Notifications
        </h2>
      </div>
      {!preview.length ? (
        <p className={styles.muted}>You&apos;re all caught up.</p>
      ) : (
        <ul className={styles.notifyPreview}>
          {preview.map((n) => (
            <li key={n.notificationId}>
              {n.href ? (
                <Link className={styles.notifyRow} to={n.href}>
                  <strong>{n.title}</strong>
                  <span>{n.body}</span>
                </Link>
              ) : (
                <div className={styles.notifyRow}>
                  <strong>{n.title}</strong>
                  <span>{n.body}</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function RegistrationCard({ row }) {
  if (!row) return null;
  return (
    <Link className={styles.regCard} to={`/browse/${row.competitionId}`}>
      <h3>{row.name}</h3>
      <p className={styles.muted}>
        {row.status}
        {row.eventNames?.length ? ` · ${row.eventNames.join(", ")}` : ""}
      </p>
    </Link>
  );
}

/** Locked-draw match for each event the player is still in. Opens the fixture graph. */
export function HomeDrawBannerList({ banners }) {
  if (!banners?.length) return null;
  return (
    <section className={styles.bannerStack} aria-label="Your tournaments">
      {banners.map((banner) => (
        <Link
          key={banner.drawId || banner.fixtureId || banner.competitionId}
          to={banner.href}
          state={{ yourMatch: banner.detail }}
          className={styles.drawBanner}
          data-testid="home-draw-banner"
        >
          <span className={styles.badge}>{banner.badge || "Draw locked"}</span>
          <span className={styles.bannerTournament}>{banner.competitionName || "Tournament"}</span>
          {banner.eventName ? <span className={styles.bannerEvent}>{banner.eventName}</span> : null}
          <span className={styles.bannerDetail}>{banner.detail}</span>
          <span className={styles.bannerCta}>{banner.cta || "Fixture graph"}</span>
        </Link>
      ))}
    </section>
  );
}

/** Fixed draw match for a player who is already in the tournament. */
export function HomeNextMatchSection({ fixture }) {
  if (!fixture) return null;
  const bits = [fixture.eventName, fixture.roundName, fixture.courtLabel].filter(Boolean);
  return (
    <section aria-labelledby="home-next-match-heading">
      <div className={styles.sectionHead}>
        <h2 id="home-next-match-heading" className={styles.sectionTitle}>
          Next match
        </h2>
      </div>
      <NextMatchCard
        card={{
          badge: fixture.urgency === 0 ? "On court" : "Next match",
          title: `vs ${fixture.opponentLabel || "Opponent"}`,
          subtitle: bits.join(" · ") || fixture.competitionName || "Tournament",
          meta: bits.length ? fixture.competitionName || "" : "",
          href: fixture.href || "/tournaments",
          ctaLabel: "Open",
        }}
      />
    </section>
  );
}

export function MyCompetitionsSection({ current = [], upcoming = [] }) {
  const all = [...current, ...upcoming];
  if (!all.length) return null;
  return (
    <section aria-labelledby="my-comps-heading">
      <div className={styles.sectionHead}>
        <h2 id="my-comps-heading" className={styles.sectionTitle}>
          My tournaments
        </h2>
        <Link className={styles.linkAction} to="/tournaments">
          Manage
        </Link>
      </div>
      <ul className={styles.listStack}>
        {all.slice(0, 4).map((r) => (
          <li key={r.competitionId}>
            <RegistrationCard row={r} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export function MyMatchesSection({ matches = [] }) {
  return (
    <section aria-labelledby="my-matches-heading">
      <div className={styles.sectionHead}>
        <h2 id="my-matches-heading" className={styles.sectionTitle}>
          My matches
        </h2>
        <Link className={styles.linkAction} to="/my-player">
          Dashboard
        </Link>
      </div>
      {!matches.length ? (
        <p className={styles.muted}>Completed matches appear here after you finish play.</p>
      ) : (
        <ul className={styles.listStack}>
          {matches.slice(0, 5).map((m) => (
            <li key={m.matchId}>
              <Link className={styles.regCard} to={`/history/match/${m.matchId}`}>
                <h3>{m.opponentsLabel || m.resultHeadline || "Match"}</h3>
                <p className={styles.muted}>
                  {m.isWin ? "Win" : "Loss"}
                  {m.scoreSummary ? ` · ${m.scoreSummary}` : ""}
                  {m.durationLabel ? ` · ${m.durationLabel}` : ""}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
